/**
 * Deep links into a pre-configured conversation, and the parser that reads
 * them back.
 *
 * The client only ever passes IDS AND CHOICES: which authored scenario, which
 * persona key, which NPC level, Development or Testing, and (for an exam) which
 * domain the finished session should be linked to. The server resolves every
 * piece of content from those ids and refuses anything the caller may not use
 * (an invisible scenario, a locked level, Testing without an authored rubric).
 */

import type { PracticeMode } from '../store/api/coachingApi';

/** A Testing conversation on an authored exam scenario, linked to the exam on finalize. */
export function examConversationUrl(scenarioId: string, domainKey: string): string {
  const q = new URLSearchParams({ scenarioId, mode: 'testing', examDomain: domainKey });
  return `/coaching?${q.toString()}`;
}

/** A conversation bound to an NPC ladder level (persona KEY + level id). */
export function levelConversationUrl(personaKey: string, npcLevelId: string): string {
  const q = new URLSearchParams({ persona: personaKey, npcLevelId });
  return `/coaching?${q.toString()}`;
}

export type CoachingPreset =
  | { kind: 'none' }
  | { kind: 'scenario'; scenarioId: string; mode: PracticeMode; examDomain: string | null }
  | { kind: 'level'; personaKey: string; npcLevelId: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY = /^[a-z0-9_-]{1,100}$/i;

/**
 * Read CoachingPage's query params. Malformed values are ignored (the page
 * falls back to its normal selection UI) rather than sent to the server.
 * An exam link is always Testing: an exam only counts graded attempts.
 */
export function parseCoachingPreset(params: URLSearchParams): CoachingPreset {
  const npcLevelId = params.get('npcLevelId');
  const persona = params.get('persona');
  if (npcLevelId && persona && UUID.test(npcLevelId) && KEY.test(persona)) {
    return { kind: 'level', personaKey: persona, npcLevelId };
  }
  const scenarioId = params.get('scenarioId');
  if (scenarioId && UUID.test(scenarioId)) {
    const examDomainRaw = params.get('examDomain');
    const examDomain = examDomainRaw && KEY.test(examDomainRaw) ? examDomainRaw : null;
    const mode: PracticeMode = examDomain || params.get('mode') === 'testing' ? 'testing' : 'development';
    return { kind: 'scenario', scenarioId, mode, examDomain };
  }
  return { kind: 'none' };
}

/**
 * The scenarios authored for an exam: those whose tags include one of the
 * exam definition's relatedScenarioKeys — the same rule the server applies
 * when it decides whether a conversation counts toward the exam.
 */
export function scenariosForExam<T extends { tags?: string[] | null }>(
  exam: { relatedScenarioKeys: readonly string[] },
  scenarios: readonly T[]
): T[] {
  return scenarios.filter((s) => (s.tags ?? []).some((t) => exam.relatedScenarioKeys.includes(t)));
}

/** Human duration for exam requirements, e.g. 185 → "3m 05s". */
export function formatSeconds(total: number): string {
  const s = Math.max(0, Math.floor(total));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r.toString().padStart(2, '0')}s` : `${r}s`;
}
